<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ExclamationIcon, CheckCircleIcon } from '@heroicons/vue/solid';

import { MetaConnectionStatusResult } from '@/types';

const props = defineProps<{ connection: MetaConnectionStatusResult }>();
const emit = defineEmits<{ (event: 'confirm', connectionId: string): void }>();
const { t } = useI18n();

const needsConfirmation = computed(() =>
    ['awaiting_confirmation', 'pending'].includes(props.connection.status || ''),
);
const hasRegisteredPhone = computed(() =>
    props.connection.phoneNumbers.some(
        (phone) => phone.isActive && phone.registrationStatus === 'REGISTERED',
    ),
);
</script>

<template>
    <UiPanel v-if="connection.status" class="mt-6">
        <div v-if="needsConfirmation" class="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div class="flex gap-3">
                <ExclamationIcon class="h-5 w-5 flex-none text-amber-700" />
                <div class="text-sm text-amber-900">
                    <p class="font-semibold">
                        {{ t('whatsappMeta.assetStatus.confirmationNotice') }}
                    </p>
                    <p v-if="connection.hasOtherSubscribedApps" class="mt-1">
                        {{ t('whatsappMeta.assetStatus.otherAppsSubscribed') }}
                    </p>
                </div>
            </div>
        </div>

        <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div>
                <p class="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {{ t('whatsappMeta.assetStatus.connectedWaba') }}
                </p>
                <p class="text-sm font-semibold text-gray-900">
                    {{ connection.displayName || connection.wabaId }}
                </p>
            </div>
            <span
                class="rounded-full px-3 py-1 text-xs font-medium"
                :class="
                    needsConfirmation
                        ? 'bg-amber-100 text-amber-800'
                        : hasRegisteredPhone
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                "
            >
                {{
                    needsConfirmation
                        ? t('whatsappMeta.assetStatus.status.awaitingConfirmation')
                        : hasRegisteredPhone
                          ? t('whatsappMeta.assetStatus.status.connected')
                          : t('whatsappMeta.assetStatus.status.ready')
                }}
            </span>
        </div>

        <ul class="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-200">
            <li
                v-for="phone in connection.phoneNumbers"
                :key="phone.phoneNumberId || phone.displayPhoneNumber"
                class="flex items-center justify-between gap-3 px-4 py-3 text-sm"
            >
                <span
                    ><strong class="text-gray-900">{{
                        phone.displayPhoneNumber || t('whatsappMeta.assetStatus.whatsappNumber')
                    }}</strong
                    ><span v-if="phone.verifiedName" class="ml-2 text-gray-500">{{
                        phone.verifiedName
                    }}</span></span
                >
                <CheckCircleIcon
                    v-if="phone.isActive && phone.registrationStatus === 'REGISTERED'"
                    class="h-5 w-5 text-emerald-600"
                    :aria-label="t('whatsappMeta.assetStatus.status.active')"
                />
            </li>
        </ul>

        <div class="mt-4 flex flex-wrap gap-4 text-xs text-gray-600">
            <span
                >{{ t('whatsappMeta.assetStatus.systemUser') }}
                {{
                    connection.systemUserAssigned
                        ? t('whatsappMeta.assetStatus.configured')
                        : t('whatsappMeta.assetStatus.pending')
                }}</span
            >
            <span
                >Webhook:
                {{
                    connection.webhookSubscribed
                        ? t('whatsappMeta.assetStatus.configured')
                        : t('whatsappMeta.assetStatus.pending')
                }}</span
            >
        </div>

        <UiButton
            v-if="needsConfirmation && connection.connectionId"
            variant="primary"
            text-variant="white"
            class="mt-5"
            @click="emit('confirm', connection.connectionId)"
        >
            {{ t('whatsappMeta.assetStatus.confirm') }}
        </UiButton>
    </UiPanel>
</template>
